import type { PlaybackState } from '@fcbh/audio';
import { playback } from '../state/store.js';
import { acquireWakeLock, releaseWakeLock } from './wake-lock.js';

/**
 * Thin wrapper around HTMLAudioElement that mirrors playback into the global
 * signal and registers Media Session handlers so the lock screen exposes
 * Play/Pause/Seek to the OS.
 *
 * Maintains two Audio elements:
 *   el  — the currently active (playing) element
 *   buf — a background prefetch element that buffers the next chapter
 *
 * Call prefetch(url) while a chapter is playing; call swapBuffer() on chapter
 * end to instantly hand off to the pre-buffered element with no HTTP round-trip.
 */
export class Player {
  private el: HTMLAudioElement;
  private buf: HTMLAudioElement;

  /** Sync event listeners on the active element — rebuilt on every swap. */
  private activeListeners: Array<[string, EventListener]> = [];

  /**
   * Sticky listeners (ended, error) — survive element swaps.
   * Registered once via onEnded()/onError(); re-attached to the new active
   * element automatically after every swapBuffer() call.
   */
  private readonly stickyListeners = new Map<string, EventListener>();

  constructor() {
    this.el  = this.makeEl();
    this.buf = this.makeEl();
    this.bindEvents();
    this.bindMediaSession();
  }

  private makeEl(): HTMLAudioElement {
    const el = new Audio();
    el.preload      = 'auto';   // start buffering as soon as src is set
    el.crossOrigin  = 'anonymous';
    return el;
  }

  // ── Public element access ────────────────────────────────────────────────

  /** The currently active HTMLAudioElement — may change after swapBuffer(). */
  get element(): HTMLAudioElement {
    return this.el;
  }

  // ── Sticky event registration (survived across swaps) ────────────────────

  /** Register a callback for the 'ended' event. Survives element swaps. */
  onEnded(cb: () => void): void {
    const fn = cb as EventListener;
    this.stickyListeners.set('ended', fn);
    this.el.addEventListener('ended', fn);
  }

  /** Register a callback for the 'error' event. Survives element swaps. */
  onError(cb: () => void): void {
    const fn = cb as EventListener;
    this.stickyListeners.set('error', fn);
    this.el.addEventListener('error', fn);
  }

  // ── Prefetch / swap ───────────────────────────────────────────────────────

  /**
   * Begin buffering a URL in the background element.
   * Idempotent — no-op if this URL is already being buffered.
   */
  prefetch(url: string): void {
    if (!url || this.buf.src === url) return;
    this.buf.src = url;
    // preload='auto' is already set on buf; the browser starts fetching automatically.
  }

  /**
   * Swap the background buffer into the active position.
   * Returns true when the swap succeeded (buf had HAVE_CURRENT_DATA or better).
   * After a successful swap the caller should call play() directly — no
   * setSource() needed.
   */
  swapBuffer(): boolean {
    if (!this.buf.src || this.buf.readyState < 2 /* HAVE_CURRENT_DATA */) return false;
    this.buf.pause(); // safety — buf should never be playing

    // Remove ALL tracked listeners from the element about to become buffer.
    for (const [type, fn] of this.activeListeners) {
      this.el.removeEventListener(type, fn);
    }
    for (const [type, fn] of this.stickyListeners) {
      this.el.removeEventListener(type, fn);
    }
    this.activeListeners = [];

    // Swap active ↔ buffer references.
    [this.el, this.buf] = [this.buf, this.el];

    // Release the old active element (now in buf slot).
    this.buf.removeAttribute('src');
    this.buf.load();

    // Re-bind all listeners to the new active element.
    this.bindEvents();

    return true;
  }

  // ── Playback controls ─────────────────────────────────────────────────────

  setSource(url: string): void {
    if (this.el.src === url) return;
    if (!url) {
      this.el.removeAttribute('src');
      this.el.load();
      return;
    }
    this.el.src = url;
  }

  play(): Promise<void> {
    return this.el.play();
  }

  pause(): void {
    this.el.pause();
  }

  isPlaying(): boolean {
    return !this.el.paused;
  }

  seek(sec: number): void {
    this.el.currentTime = Math.max(0, sec);
  }

  seekBy(deltaSec: number): void {
    this.seek(this.el.currentTime + deltaSec);
  }

  /**
   * Seek as soon as the source has enough metadata to honor the request.
   * Used by the resume-position flow — a `currentTime =` set before
   * `loadedmetadata` fires is silently dropped by HTMLAudioElement.
   */
  seekOnLoad(sec: number): void {
    if (Number.isFinite(this.el.duration) && this.el.duration > 0) {
      this.seek(sec);
      return;
    }
    const onReady = () => {
      this.el.removeEventListener('loadedmetadata', onReady);
      this.seek(sec);
    };
    this.el.addEventListener('loadedmetadata', onReady);
  }

  setRate(rate: number): void {
    this.el.playbackRate = rate;
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private bindEvents(): void {
    // Build a fresh sync handler that always reads from this.el.
    // Arrow function captures `this` lexically — reference stays correct
    // even after future swaps that call bindEvents() again.
    const sync = (): void => {
      const el = this.el;
      const next: PlaybackState = {
        playing:     !el.paused,
        currentSec:  el.currentTime,
        durationSec: Number.isFinite(el.duration) ? el.duration : 0,
        rate:        el.playbackRate,
        bufferedSec: el.buffered.length > 0 ? el.buffered.end(el.buffered.length - 1) : 0,
      };
      playback.value = next;

      // Background-audio lifecycle. Acquire the screen wake lock while audio is
      // actively playing so a PWA put down with the screen off keeps going;
      // release it the moment playback pauses/ends. Mirror the same state into
      // MediaSession so the OS exposes correct lock-screen transport + scrubber.
      if (next.playing) {
        acquireWakeLock();
      } else {
        releaseWakeLock();
      }
      this.syncMediaSession(next);
    };

    const add = (type: string, fn: EventListener): void => {
      this.el.addEventListener(type, fn);
      this.activeListeners.push([type, fn]);
    };

    add('timeupdate',    sync);
    add('play',          sync);
    add('pause',         sync);
    add('loadedmetadata', sync);
    add('progress',      sync);
    add('ratechange',    sync);

    // Re-attach sticky listeners (ended, error) registered via onEnded/onError.
    for (const [type, fn] of this.stickyListeners) {
      this.el.addEventListener(type, fn);
    }
  }

  private bindMediaSession(): void {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    // These arrow functions capture `this` and call through to the current
    // this.el at invocation time — no rebinding needed after swaps.
    ms.setActionHandler('play',         () => { void this.play(); });
    ms.setActionHandler('pause',        () => this.pause());
    ms.setActionHandler('stop',         () => this.pause());
    ms.setActionHandler('seekto',       (d) => { if (typeof d.seekTime === 'number') this.seek(d.seekTime); });
    ms.setActionHandler('seekbackward', (d) => this.seek(this.el.currentTime - (d.seekOffset ?? 10)));
    ms.setActionHandler('seekforward',  (d) => this.seek(this.el.currentTime + (d.seekOffset ?? 10)));
  }

  /**
   * Mirror playback state into the OS Media Session so the lock-screen / system
   * media controls reflect the right play/pause icon and scrubber position.
   * Driven from the same sync handler that updates the playback signal, so it
   * stays correct whether playback was toggled from the UI, the lock screen,
   * or chapter auto-advance. setPositionState is best-effort (older browsers /
   * transient non-finite durations throw — swallow and move on).
   */
  private syncMediaSession(state: PlaybackState): void {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    ms.playbackState = state.playing ? 'playing' : 'paused';
    if (state.durationSec > 0 && Number.isFinite(state.durationSec)) {
      try {
        ms.setPositionState({
          duration: state.durationSec,
          playbackRate: state.rate > 0 ? state.rate : 1,
          position: Math.min(state.currentSec, state.durationSec),
        });
      } catch {
        // setPositionState unsupported or rejected the values — non-fatal.
      }
    }
  }
}
